import { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Menu, X, Filter, Star, Heart, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TagFilterDropdown } from '@/components/TagFilterDropdown';
import { FolderFilterDropdown } from '@/components/FolderFilterDropdown';
import { ColorFilterDropdown } from '@/components/ColorFilterDropdown';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

interface MobileToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  selectedFolders?: string[];
  onFoldersChange?: (folders: string[]) => void;
  selectedColors?: string[];
  onColorsChange?: (colors: string[]) => void;
  includeSimilarColors?: boolean;
  onIncludeSimilarColorsChange?: (includeSimilar: boolean) => void;
  ratingFilter?: { operator: 'gte' | 'lte' | 'eq'; value: number } | null;
  onRatingFilterChange?: (filter: { operator: 'gte' | 'lte' | 'eq'; value: number } | null) => void;
  photoTypeFilter?: 'all' | 'has_pii' | 'has_tag' | 'no_tags' | 'no_folders' | 'has_code' | 'has_person' | 'is_illustrated' | 'has_error' | null;
  onPhotoTypeFilterChange?: (filter: 'all' | 'has_pii' | 'has_tag' | 'no_tags' | 'no_folders' | 'has_code' | 'has_person' | 'is_illustrated' | 'has_error' | null) => void;
  favoriteFilter?: boolean;
  onFavoriteFilterChange?: (favorite: boolean) => void;
  availableTags?: Array<{ id: string; name: string; color?: string }>;
  availableFolders?: Array<{ id: string; name: string; color?: string }>;
  organizationId?: string;
  onTagCreated?: () => void;
  thumbnailSize?: 'small' | 'medium' | 'large';
  onThumbnailSizeChange?: (size: 'small' | 'medium' | 'large') => void;
  sidebarContent: React.ReactNode;
}

export function MobileToolbar({
  searchQuery,
  onSearchChange,
  currentPage,
  totalPages,
  onPageChange,
  selectedTags = [],
  onTagsChange,
  selectedFolders = [],
  onFoldersChange,
  selectedColors = [],
  onColorsChange,
  includeSimilarColors = false,
  onIncludeSimilarColorsChange,
  ratingFilter,
  onRatingFilterChange,
  photoTypeFilter = 'all',
  onPhotoTypeFilterChange,
  favoriteFilter = false,
  onFavoriteFilterChange,
  availableTags = [],
  availableFolders = [],
  organizationId,
  onTagCreated,
  thumbnailSize = 'medium',
  onThumbnailSizeChange,
  sidebarContent,
}: MobileToolbarProps) {
  const [showSearchFilters, setShowSearchFilters] = useState(false);

  return (
    <div className="md:hidden flex flex-col border-b bg-card">
      {/* Top row: Hamburger menu + Page navigation + Search toggle */}
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <Drawer>
          <DrawerTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9">
              <Menu className="h-5 w-5" />
            </Button>
          </DrawerTrigger>
          <DrawerContent className="max-h-[85vh]">
            <div className="overflow-y-auto">
              {sidebarContent}
            </div>
          </DrawerContent>
        </Drawer>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="h-9 w-9"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>

          <span className="text-sm font-medium min-w-[60px] text-center">
            {currentPage} / {totalPages}
          </span>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="h-9 w-9"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSearchFilters(!showSearchFilters)}
            className="h-9 w-9"
          >
            <Search className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Search and Filters - Collapsible */}
      {showSearchFilters && (
        <>
          {/* Search row */}
          <div className="px-3 py-2 border-b">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search photos..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-9 h-10 w-full"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onSearchChange('')}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

          {/* Filters row */}
          <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto">
            {/* Filter Type */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0 relative">
                  <Filter className="h-4 w-4" />
                  {photoTypeFilter && photoTypeFilter !== 'all' && (
                    <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
                      1
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40 bg-background z-50">
                <DropdownMenuLabel>Filter Type</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('all')} className="relative">
                  {photoTypeFilter === 'all' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'all' ? 'ml-6' : ''}>All</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_pii')} className="relative">
                  {photoTypeFilter === 'has_pii' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'has_pii' ? 'ml-6' : ''}>Has PII</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('no_tags')} className="relative">
                  {photoTypeFilter === 'no_tags' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'no_tags' ? 'ml-6' : ''}>No Tags</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('no_folders')} className="relative">
                  {photoTypeFilter === 'no_folders' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'no_folders' ? 'ml-6' : ''}>No Folders</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_tag')} className="relative">
                  {photoTypeFilter === 'has_tag' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'has_tag' ? 'ml-6' : ''}>Has Tag</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_code')} className="relative">
                  {photoTypeFilter === 'has_code' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'has_code' ? 'ml-6' : ''}>Code</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_person')} className="relative">
                  {photoTypeFilter === 'has_person' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'has_person' ? 'ml-6' : ''}>Person</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('is_illustrated')} className="relative">
                  {photoTypeFilter === 'is_illustrated' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'is_illustrated' ? 'ml-6' : ''}>Illustrated</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_error')} className="relative">
                  {photoTypeFilter === 'has_error' && <Check className="absolute left-2 h-4 w-4" />}
                  <span className={photoTypeFilter === 'has_error' ? 'ml-6' : ''}>Error</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <ColorFilterDropdown
              selectedColors={selectedColors}
              onColorsChange={onColorsChange || (() => {})}
              includeSimilar={includeSimilarColors}
              onIncludeSimilarChange={onIncludeSimilarColorsChange || (() => {})}
              organizationId={organizationId || ''}
            />

            <TagFilterDropdown
              selectedTags={selectedTags}
              onTagsChange={onTagsChange || (() => {})}
              availableTags={availableTags}
              organizationId={organizationId || ''}
              onTagCreated={onTagCreated}
            />

            <FolderFilterDropdown
              selectedFolders={selectedFolders}
              onFoldersChange={onFoldersChange || (() => {})}
              organizationId={organizationId}
            />

            {/* Star Rating Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0 relative">
                  <Star className="h-4 w-4" />
                  {ratingFilter && (
                    <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
                      1
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                <DropdownMenuLabel>Filter by Rating</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 5 })}>
                  ★★★★★ (5 stars)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 4 })}>
                  ★★★★ (4+ stars)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 3 })}>
                  ★★★ (3+ stars)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 2 })}>
                  ★★ (2+ stars)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 1 })}>
                  ★ (1+ stars)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'lte', value: 1 })}>
                  ≤ 1 star
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'lte', value: 2 })}>
                  ≤ 2 stars
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'lte', value: 3 })}>
                  ≤ 3 stars
                </DropdownMenuItem>
                {ratingFilter && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onRatingFilterChange?.(null)}>
                      Clear filter
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Heart/Favorite Filter */}
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 flex-shrink-0 relative ${favoriteFilter ? 'text-red-500' : ''}`}
              onClick={() => onFavoriteFilterChange?.(!favoriteFilter)}
            >
              <Heart className={`h-4 w-4 ${favoriteFilter ? 'fill-current' : ''}`} />
              {favoriteFilter && (
                <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
                  1
                </Badge>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
